/* `/notifications/types` and `/notifications/settings` — which notices exist and
   who wants them on which channel — and the bell's inbox. Wire: backend/modules/notifications/README.md.

   In `shared/api/` because three places use it: workspace settings edits the main
   settings, an agent's settings edits that agent's overrides, the sidebar's bell lists notices.

   `agent_id: null` is the main settings. Paths are bare; `client.baseURL` carries
   `/api/v1`. */
import { useInfiniteQuery, useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { ApiError } from "./errors";
import { useSettings } from "./settings";

export type Channel = "inbox" | "email";
export type SettingValue = "on" | "off" | "follow";

export interface NotificationType {
  id: string;
  label: string;
  /** A short description of when this notice is sent. */
  description: string;
  /** Only the channels this type is sent on. */
  defaults: Partial<Record<Channel, boolean>>;
  per_agent: boolean;
}

export interface SettingCell {
  type: string;
  channel: Channel;
  value: SettingValue;
  /** What happens now. Under `follow`, the answer taken from the main settings. */
  enabled: boolean;
}

export interface NotificationSettings {
  agent_id: string | null;
  settings: SettingCell[];
}

export interface SettingChange {
  type: string;
  channel: Channel;
  value: SettingValue;
}

export interface ChangeBody {
  agent_id?: string;
  changes: SettingChange[];
}

/** One notice in the bell. `payload` holds its kind's facts; `link` is `/?org=<org>#/<screen>`. */
export interface Notice {
  id: string;
  type: string;
  agent_id: string | null;
  link: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export const NOTICE_PAGE = 20;

const changeKey = ["notifications", "change"] as const;

export const notificationKeys = {
  all: ["notifications"] as const,
  types: () => ["notifications", "types"] as const,
  allSettings: ["notifications", "settings"] as const,
  settings: (orgId: string, agentId: string | null) => ["notifications", "settings", orgId, agentId] as const,
  inbox: ["notifications", "inbox"] as const,
  notices: (orgId: string) => ["notifications", "inbox", orgId, "list"] as const,
  unread: (orgId: string) => ["notifications", "inbox", orgId, "unread"] as const,
};

/* A refused save names its error code, so a person can quote it. The app-wide toast leaves the code out. */
function reportFailure(err: unknown): void {
  const api = err instanceof ApiError ? err : null;
  const detail = [api?.code, api?.request_id && `ref ${api.request_id}`].filter(Boolean).join(" · ");
  toast.error(err instanceof Error ? err.message : "Something went wrong", { description: detail || undefined });
}

/* ------------------------------ endpoints -------------------------------- */

export async function listNotificationTypes(): Promise<NotificationType[]> {
  return unwrap(await client.get<Envelope<NotificationType[]>>("/notifications/types"));
}

export async function getNotificationSettings(agentId?: string | null): Promise<NotificationSettings> {
  const params = agentId ? { agent_id: agentId } : undefined;
  return unwrap(await client.get<Envelope<NotificationSettings>>("/notifications/settings", { params }));
}

/** Answers the full state after the change. */
export async function changeNotificationSettings(body: ChangeBody): Promise<NotificationSettings> {
  return unwrap(await client.put<Envelope<NotificationSettings>>("/notifications/settings", body));
}

/** Puts every cell of the agent back to `follow`. Answers 204, so there is nothing to unwrap. */
export async function resetAgentNotifications(agentId: string): Promise<void> {
  await client.delete("/notifications/settings", { params: { agent_id: agentId } });
}

/** Newest first; the next page starts after `after`, the last notice of the one before. */
export async function listNotices(after?: Notice): Promise<Notice[]> {
  const params = { limit: NOTICE_PAGE, ...(after && { before: after.created_at, before_id: after.id }) };
  return unwrap(await client.get<Envelope<Notice[]>>("/notifications", { params }));
}

export async function getUnreadCount(): Promise<number> {
  return unwrap(await client.get<Envelope<{ count: number }>>("/notifications/unread-count")).count;
}

export async function markNoticeRead(id: string): Promise<void> {
  await client.post(`/notifications/${id}/read`);
}

export async function markAllNoticesRead(): Promise<void> {
  await client.post("/notifications/read-all");
}

/* -------------------------------- hooks ---------------------------------- */

/* The inbox is keyed by org and a sign-in sees only its workspace's; signing out clears
   the whole cache, so the next person never sees the last one's notices. */
export function useUnreadCount() {
  const orgId = useSettings().data?.org?.id;
  return useQuery({ queryKey: notificationKeys.unread(orgId ?? ""), queryFn: getUnreadCount, enabled: !!orgId });
}

export function useNotices(open: boolean) {
  const orgId = useSettings().data?.org?.id;
  return useInfiniteQuery({
    queryKey: notificationKeys.notices(orgId ?? ""),
    queryFn: ({ pageParam }) => listNotices(pageParam),
    initialPageParam: undefined as Notice | undefined,
    getNextPageParam: (page) => (page.length === NOTICE_PAGE ? page[page.length - 1] : undefined),
    enabled: !!orgId && open,
    meta: { silent: true },
  });
}

export function useMarkNoticeRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNoticeRead,
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.inbox }),
  });
}

export function useMarkAllNoticesRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNoticesRead,
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.inbox }),
  });
}

export function useNotificationTypes() {
  return useQuery({ queryKey: notificationKeys.types(), queryFn: listNotificationTypes, staleTime: 5 * 60_000, meta: { silent: true } });
}

/* Keyed by org, so a workspace switch never shows the last one's settings. */
export function useNotificationSettings(agentId: string | null) {
  const orgId = useSettings().data?.org?.id;
  return useQuery({
    queryKey: notificationKeys.settings(orgId ?? "", agentId),
    queryFn: () => getNotificationSettings(agentId),
    enabled: !!orgId,
    meta: { silent: true },
  });
}

export function useNotificationSettingsSaving() {
  return useIsMutating({ mutationKey: changeKey }) > 0;
}

/* Changes and resets of the same settings run in order. Optimism starts with
   each request, so an earlier failure cannot roll back a queued choice.
   A main change also changes what each agent's `follow` means. */
export function useChangeNotificationSettings(agentId: string | null) {
  const qc = useQueryClient();
  const orgId = useSettings().data?.org?.id ?? "";
  const key = notificationKeys.settings(orgId, agentId);
  return useMutation({
    mutationKey: [...changeKey, orgId, agentId],
    scope: { id: key.join(":") },
    meta: { silent: true },
    mutationFn: async (changes: SettingChange[]) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<NotificationSettings>(key);
      if (before) {
        qc.setQueryData<NotificationSettings>(key, {
          ...before,
          settings: before.settings.map((cell) => {
            const change = changes.find((c) => c.type === cell.type && c.channel === cell.channel);
            if (!change) return cell;
            return { ...cell, value: change.value, enabled: change.value === "follow" ? cell.enabled : change.value === "on" };
          }),
        });
      }
      try {
        return await changeNotificationSettings({ ...(agentId && { agent_id: agentId }), changes });
      } catch (error) {
        if (before) qc.setQueryData(key, before);
        throw error;
      }
    },
    onError: reportFailure,
    // This save still counts as in flight here. A re-read while another save is
    // pending would show the server's older answer and flicker the switch.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: changeKey }) === 1) qc.invalidateQueries({ queryKey: notificationKeys.allSettings });
    },
  });
}

export function useResetAgentNotifications(agentId: string) {
  const qc = useQueryClient();
  const orgId = useSettings().data?.org?.id ?? "";
  return useMutation({
    mutationKey: [...changeKey, orgId, agentId],
    scope: { id: notificationKeys.settings(orgId, agentId).join(":") },
    mutationFn: () => resetAgentNotifications(agentId),
    meta: { silent: true },
    onError: reportFailure,
    onSettled: () => {
      if (qc.isMutating({ mutationKey: changeKey }) === 1) qc.invalidateQueries({ queryKey: notificationKeys.allSettings });
    },
  });
}
